// The story's narration.
//
// A native <audio controls> rather than a custom play/pause button: shadowing a
// story means replaying one paragraph, and the seek bar, time display, volume
// and keyboard support come free and correct. The browser styles the control
// itself, so it will not match the dark theme exactly — worth it for scrubbing
// that actually works.
//
// Source order matters. The public GCS URL is preferred so no token travels in a
// URL; the same-origin route is the fallback for a story whose MP3 exists in
// english/audio but has not been uploaded yet — which is exactly the story you
// generated this morning and want to hear now. That route reads ?token= because
// an <audio> element cannot send an Authorization header.

import { useRef, useState } from "react";
import { TOKEN_KEY } from "../../services/api";

const SPEEDS = [0.75, 1];

export default function StoryAudio({ slug, hasAudio, audioUrl }) {
  const ref = useRef(null);
  const [speed, setSpeed] = useState(1);

  if (!hasAudio) {
    return (
      <div className="rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3 text-xs text-slate-500">
        No narration yet — generate it with the <code>audio-generate</code> skill.
      </div>
    );
  }

  const token = localStorage.getItem(TOKEN_KEY);
  const src =
    audioUrl ||
    `/api/english/topics/${encodeURIComponent(slug)}/audio${token ? `?token=${encodeURIComponent(token)}` : ""}`;

  const applySpeed = (rate) => {
    setSpeed(rate);
    if (ref.current) ref.current.playbackRate = rate;
  };

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
      <audio
        ref={ref}
        src={src}
        controls
        preload="metadata"
        className="w-full"
        onLoadedMetadata={() => {
          if (ref.current) ref.current.playbackRate = speed;
        }}
      />
      <div className="mt-3 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-slate-600">Speed</span>
        {SPEEDS.map((rate) => (
          <button
            key={rate}
            type="button"
            onClick={() => applySpeed(rate)}
            className={`rounded-md px-2 py-0.5 text-xs transition ${
              speed === rate
                ? "bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/40"
                : "text-slate-500 hover:text-slate-300"
            }`}
          >
            {rate}×
          </button>
        ))}
        <span className="ml-auto hidden text-[10px] text-slate-600 sm:inline">
          Slow it down to shadow a line, then match it at full speed.
        </span>
      </div>
    </div>
  );
}
